import { FileUp } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';

import { downloadSampleCsvFromUrl } from '~features/import-wizard/download-csv';

import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';

export interface ImportWizardCsvSelectorProps {
  file: File | null;
  setFile: (file: File | null) => void;
  onNext: () => void;
  title: string;
  description?: string;
  sampleCsvUrl: string;
  sampleCsvDownloadName: string;
}

export function ImportWizardCsvSelector({
  file,
  setFile,
  onNext,
  title,
  description,
  sampleCsvUrl,
  sampleCsvDownloadName,
}: ImportWizardCsvSelectorProps) {
  const [downloadingSample, setDownloadingSample] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: Array<File>) => {
      const nextFile = acceptedFiles[0];
      if (nextFile) setFile(nextFile);
    },
    [setFile],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'] },
    multiple: false,
  });

  const handleDownloadSample = async () => {
    setDownloadingSample(true);
    try {
      await downloadSampleCsvFromUrl(sampleCsvUrl, sampleCsvDownloadName);
    } catch {
      toast.error('Could not download sample CSV', {
        description: 'Try again, or contact support if this keeps happening.',
      });
    } finally {
      setDownloadingSample(false);
    }
  };

  return (
    <div className='space-y-4 rounded-lg border p-4'>
      <div>
        <h3 className='font-semibold'>{title}</h3>
        {description ? (
          <p className='mt-1 text-sm text-muted-foreground'>{description}</p>
        ) : null}
      </div>
      <div
        {...getRootProps()}
        className={cn(
          'cursor-pointer rounded-lg border-2 border-dashed border-primary p-6 text-center transition-colors',
          { 'bg-accent': isDragActive },
        )}
      >
        <input {...getInputProps()} />
        <FileUp className='mx-auto mb-4 size-10 text-primary' />
        {file ? (
          <p className='font-semibold'>File selected: {file.name}</p>
        ) : (
          <p>Drag and drop your CSV here, or click to browse</p>
        )}
        <p className='mt-2 text-sm text-muted-foreground'>.csv only</p>
      </div>
      <p className='text-sm text-muted-foreground'>
        <Button
          type='button'
          variant='link'
          className='h-auto px-0'
          disabled={downloadingSample}
          onClick={() => void handleDownloadSample()}
        >
          {downloadingSample ? 'Downloading…' : 'Download sample CSV'}
        </Button>
        {' — '}
        Use this template for column names that auto-map when you upload.
      </p>
      <Button onClick={onNext} disabled={!file}>
        Continue to column mapping
      </Button>
    </div>
  );
}
